import {
  css,
  CSSResult,
  LitElement,
  nothing,
  PropertyValues,
  TemplateResult,
} from "lit";
import { MessageModal } from "../modal/MessageModal";
import { TouchDragManager } from "../../element/TouchDragManager";
import { logger } from "../../util/Logger";

export abstract class BaseElement extends LitElement {
  protected useShadowRoot: boolean = false;

  protected abstract renderHtml(): TemplateResult;
  protected parentRoot?: BaseElement;

  protected renderCss(): CSSResult | CSSResult[] {
    return css``;
  }

  firstUpdated(e: any): void {
    super.firstUpdated(e);
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.parentRoot = this.findParentRoot();
  }

  //touch settings
  private touchDragManager: TouchDragManager | undefined;
  childUpdated(child: BaseElement): void {
    if (this.useShadowRoot) {
      this.touchDragManager = new TouchDragManager(this.shadowRoot!);
      this.touchDragManager.bindEvents(child);
    }
  }

  childDisconnected(child: BaseElement): void {
    if (this.useShadowRoot) {
      this.touchDragManager?.disconnectEvent(child);
    }
  }

  protected findParentRoot(): BaseElement | undefined {
    let parentNode = this.getRootNode();
    if (parentNode instanceof ShadowRoot) return parentNode.host as BaseElement;

    return undefined;
  }

  protected getHostParent(): HTMLElement | undefined {
    if (this.parentElement) return this.parentElement;
    const root = this.getRootNode();
    if ((root as ShadowRoot).host) {
      return (root as ShadowRoot).host as HTMLElement;
    }
  }

  /***styles ****/

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    if (this.useShadowRoot) {
      setTimeout(() => {
        this.createStyleRoot();
      }, 1);
      this.parentRoot = undefined;
      return super.createRenderRoot();
    } else {
      // Shadow DOM kullanılmıyorsa, stil olaylarını gönder
      setTimeout(() => {
        this.dispatchEvent(
          new CustomEvent("style-added", {
            detail: {
              //@ts-ignore
              styles: this.constructor.styles,
            },
            bubbles: true,
            composed: true,
          })
        );
      }, 2);

      return this;
    }
  }

  updateContext(context: any, propery: string) {}

  private _childStyle?: HTMLStyleElement;
  private _addedStyles = new Set<string>();

  private loadConstructorStyles() {
    if (this.constructor.hasOwnProperty("styles") && this._childStyle) {
      try {
        const styles = (this.constructor as any).styles;
        if (styles) {
          let styleText = "";

          if (Array.isArray(styles)) {
            styleText = styles.map((style) => style.toString()).join("\n");
          } else if (styles.toString) {
            styleText = styles.toString();
          }

          if (styleText && !this._addedStyles.has(styleText)) {
            this._childStyle.textContent += styleText;
            this._addedStyles.add(styleText);
          }
        }
      } catch (err) {
        logger.error("Stil eklenirken hata oluştu:", err);
      }
    }
  }
  protected createStyleRoot() {
    if (this._childStyle) {
      return;
    }

    this._childStyle = document.createElement("style");
    this._childStyle.id = "child-styles";

    if (this.shadowRoot) {
      this.shadowRoot.appendChild(this._childStyle);
    } else {
      this.append(this._childStyle);
    }

    // Shadow DOM kullanılıyorsa direkt stilleri ekle
    if (this.useShadowRoot) {
      this.loadConstructorStyles();
    }

    // Tüm olay dinleyicileri için
    this.addEventListener("style-added", this.onStyleAdded.bind(this));
  }

  protected onStyleAdded(event: any) {
    if (event instanceof CustomEvent && this._childStyle) {
      if (!this._childStyle.parentNode) {
        this.shadowRoot?.append(this._childStyle);
      }
      const customEvent = event as CustomEvent<{ styles: string }>;

      if (customEvent.detail && customEvent.detail.styles) {
        const newStyles = customEvent.detail.styles;

        if (!this._addedStyles.has(newStyles)) {
          this._childStyle.textContent += newStyles;
          this._addedStyles.add(newStyles);
        }
      }
    }
  }

  /**messgage ***/
  protected async popModal(
    content: any,
    options: { header?: any; footer?: any } = {
      footer: nothing,
      header: nothing,
    }
  ): Promise<any> {
    if (this.shadowRoot) {
      const defaultO = {
        footer: nothing,
        header: nothing,
      };
      const _options = { ...defaultO, ...options };

      const messageModal = document.createElement(
        "message-modal"
      ) as MessageModal;
      this.shadowRoot?.appendChild(messageModal);

      const response = await messageModal.show(content, _options);
      messageModal.remove();
      return response;
    } else {
      this.findParentRoot();
      return this.parentRoot?.popModal(content, options);
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.parentRoot?.childDisconnected(this);
  }

  protected updated(_changedProperties: PropertyValues): void {
    super.updated(_changedProperties);
    if (this._renderUpdate) {
      this._renderUpdate = false;
      this.parentRoot?.childUpdated(this);
    }
  }

  private _renderUpdate: boolean = false;
  protected render(): TemplateResult {
    this._renderUpdate = true;
    return this.renderHtml();
  }
}
